import { useRender } from "@base-ui/react/use-render";
import type { ReactElement, ReactNode } from "react";

import { cardStyles } from "./Card.styles.js";

export interface CardProps {
  /** `lg` compact, `xl` one item or a small summary, `2xl` a card or region, `3xl` a page's main region. */
  radius?: "lg" | "xl" | "2xl" | "3xl";
  /** `raised` for a card on another card; `none` for a placeholder drawn by its edge. */
  fill?: "surface" | "raised" | "none";
  /** `selected` is the accent edge and fill; `dashed` a place for something not there yet. */
  edge?: "default" | "selected" | "dashed" | "none";
  /**
   * `link`: a `CardLink` on the card's name stretches over it. `button`: the card is a button,
   * so pass `render={<button type="button" onClick={…} />}`.
   */
  action?: "none" | "link" | "button";
  /**
   * `raise`: the fill steps up under the pointer, on a card that acts or that an action covers.
   * Leave it off where the fill carries meaning (a picked item's accent).
   */
  hover?: "none" | "raise";
  /** The element the card renders as, when not a `div`: `<section />`, `<li />`, `<button />`. */
  render?: ReactElement;
  /** Layout and padding: the card owns only its surface. */
  className?: string;
  children?: ReactNode;
}

/**
 * The surface every card draws on: one thing summarized (a box, an item, a battle) or one part of
 * a page. It owns the fill, the edge, the radius and how the whole card acts; its contents and
 * spacing are the caller's.
 */
export function Card({
  radius,
  fill,
  edge,
  action,
  hover,
  render,
  className,
  children,
}: CardProps) {
  return useRender({
    render,
    defaultTagName: "div",
    props: { className: cardStyles({ radius, fill, edge, action, hover, className }), children },
  });
}
